class ChatMetricsClient {
  constructor() {
    this.buffer = [];
    this.flushInterval = 5000; // 5 seconds
    this.maxBufferSize = 20;
    this.flushTimer = null;
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    // Try multiple sources for auth token: form data attribute, localStorage, or call mode data attribute
    this.authToken = document.querySelector('#chat-form')?.dataset?.authToken
      || document.querySelector('[data-auth-token]')?.dataset?.authToken
      || localStorage.getItem('jwt_token');

    // Start periodic flush
    this.startPeriodicFlush();

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush(true));
  }

  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': this.csrfToken
    };

    // Refresh token in case it was updated after construction - check all sources
    const token = document.querySelector('#chat-form')?.dataset?.authToken
      || document.querySelector('[data-auth-token]')?.dataset?.authToken
      || localStorage.getItem('jwt_token')
      || this.authToken;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }
  
  startPeriodicFlush() {
    this.flushTimer = setInterval(() => this.flush(), this.flushInterval);
  }
  
  stopPeriodicFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }
  
  // Track when user submits a message
  trackMessageSubmitted(chatId, messageId) {
    this.track({
      event: 'message_submitted',
      chat_id: chatId,
      message_id: messageId,
      timestamp: new Date().toISOString()
    });
    
    // Immediately notify server for this critical event
    this.sendToServer({
      chat_id: chatId,
      message_id: messageId,
      timestamp: new Date().toISOString()
    }, '/api/v1/chat_metrics/track_submission');
  }
  
  // Track when message is received back from server
  trackMessageReceived(chatId, messageId) {
    this.track({
      event: 'message_received',
      chat_id: chatId,
      message_id: messageId,
      timestamp: new Date().toISOString()
    });
  }
  
  // Track when first audio chunk arrives
  trackFirstAudioChunk(chatId, messageId) {
    this.track({
      event: 'first_audio_chunk',
      chat_id: chatId,
      message_id: messageId,
      timestamp: new Date().toISOString()
    });
  }
  
  // Track when message is fully delivered
  trackMessageDelivered(chatId, messageId) {
    this.track({
      event: 'message_delivered',
      chat_id: chatId,
      message_id: messageId,
      timestamp: new Date().toISOString()
    });
    
    // Notify server of delivery completion
    this.sendToServer({
      chat_id: chatId,
      message_id: messageId
    }, '/api/v1/chat_metrics/track_delivery');
  }
  
  // Track general events
  track(event) {
    this.buffer.push({
      ...event,
      client_timestamp: Date.now()
    });
    
    if (this.buffer.length >= this.maxBufferSize) {
      this.flush();
    }
  }
  
  async flush(immediate = false) {
    if (this.buffer.length === 0) return;
    
    const events = [...this.buffer];
    this.buffer = [];
    
    try {
      const method = immediate ? 'sendBeacon' : 'fetch';
      await this.sendBatch(events, method);
    } catch (error) {
      console.error('Failed to send metrics:', error);
      // Re-add events to buffer on failure
      this.buffer.unshift(...events);
    }
  }
  
  async sendBatch(events, method = 'fetch') {
    const url = '/api/v1/chat_metrics/batch';
    const data = JSON.stringify({ events });

    if (method === 'sendBeacon' && navigator.sendBeacon) {
      // Use sendBeacon for page unload - note: sendBeacon cannot include auth headers
      // so we append the token as a query param that the server can validate
      const token = document.querySelector('#chat-form')?.dataset?.authToken
        || document.querySelector('[data-auth-token]')?.dataset?.authToken
        || localStorage.getItem('jwt_token')
        || this.authToken;
      const urlWithAuth = token ? `${url}?auth_token=${encodeURIComponent(token)}` : url;
      const blob = new Blob([data], { type: 'application/json' });
      navigator.sendBeacon(urlWithAuth, blob);
    } else {
      // Use fetch for normal operations
      await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: data
      });
    }
  }
  
  async sendToServer(data, endpoint) {
    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('Failed to send metric to server:', error);
    }
  }
  
  // Cleanup
  destroy() {
    this.stopPeriodicFlush();
    this.flush(true);
  }
}

// Export as singleton
const chatMetricsClient = new ChatMetricsClient();
export default chatMetricsClient;